// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import NavBar from './components/NavBar';
import CartDrawer from './components/CartDrawer';
import LegalWatermark from './components/LegalWatermark';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CouponsPage from './pages/CouponsPage';
import LoyaltyPage from './pages/LoyaltyPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderTrackingPage from './pages/OrderTrackingPage';
import AccountPage from './pages/AccountPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function StoreLayout({ children }) {
  return (
    <>
      <NavBar />
      <CartDrawer />
      <main style={{ minHeight: 'calc(100vh - 64px)', paddingBottom: 48 }}>
        {children}
      </main>
      <footer style={{
        background: '#14532D', color: '#BBF7D0', padding: '32px 24px',
        textAlign: 'center', fontSize: 13,
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 4 }}>
            🛒 Main Street Grocery
          </div>
          <div>Fresh produce, local favorites, delivered to your door.</div>
          <div style={{ marginTop: 12, color: '#86EFAC', fontSize: 11 }}>
            Powered by Miskulin Technologies · © 2026
          </div>
        </div>
      </footer>
    </>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <StoreLayout><LoginPage /></StoreLayout>} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <StoreLayout><ForgotPasswordPage /></StoreLayout>} />
      <Route path="/" element={<StoreLayout><HomePage /></StoreLayout>} />
      <Route path="/products" element={<StoreLayout><ProductsPage /></StoreLayout>} />
      <Route path="/products/:productId" element={<StoreLayout><ProductDetailPage /></StoreLayout>} />
      <Route path="/categories/:slug" element={<StoreLayout><ProductsPage /></StoreLayout>} />
      <Route path="/coupons" element={<StoreLayout><CouponsPage /></StoreLayout>} />
      <Route path="/loyalty" element={<StoreLayout><LoyaltyPage /></StoreLayout>} />
      <Route path="/cart" element={<StoreLayout><CartPage /></StoreLayout>} />
      <Route path="/checkout" element={<ProtectedRoute><StoreLayout><CheckoutPage /></StoreLayout></ProtectedRoute>} />
      <Route path="/orders/:orderId" element={<ProtectedRoute><StoreLayout><OrderTrackingPage /></StoreLayout></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><StoreLayout><OrderTrackingPage /></StoreLayout></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><StoreLayout><AccountPage /></StoreLayout></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <LegalWatermark appName="Main Street Grocery" />
          <AppRoutes />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
